import {Switch} from '@headlessui/react';
import {InformationCircleIcon} from '@heroicons/react/24/outline';
import * as d3 from 'd3';
import React from 'react';
import {Tooltip} from 'react-tooltip';
import {useQueryParam} from 'use-query-params';

import {useStagingForecastToggle} from 'src/contexts/StagingToggleContext';
import {useSelectedForecastTimes} from 'src/hooks/useSelectedForecastTimes';
import {ApiUserProfile} from 'src/lib/api/auth';
import {SeasonalSeriesType, SeriesType, Site} from 'src/lib/api/project';
import {
  AllColumns,
  DashboardSection,
  FLOW_OBSERVATION_SOURCES,
  FlowForecastColumns,
  FlowHistoricalDailyColumns,
  FlowObservationColumns,
  ForecastTime,
  ForecastTimesBySource,
  RateVolumeMode,
  SeasonalModelVisualMode,
  Source,
  TimeAggregation,
  WeatherColumns,
  WeatherHistoricalDailyColumns,
  isCumulativeMode,
} from 'src/lib/api/series';
import COLORS, {getRFCColors} from 'src/lib/colors';
import {
  addDays,
  addSeconds,
  addUTCDays,
  makeDateRangeQueryParam,
  makeGetSelectionForIntervalShortcuts,
  now,
} from 'src/lib/dates';
import featureFlags from 'src/lib/featureFlags';
import {
  getDefaultRateVolumeMode,
  getSeasonalHorizon,
  isInvalidLevelMode,
  makeValidRateVolumeMode,
  seasonalDashboardSection,
} from 'src/lib/siteUtils';
import * as tw from 'src/lib/tailwindClasses';

import {recordEvent} from './Analytics';
import ForecastSelect from './ForecastSelect';
import {FreezingLine} from './FreezingLine';
import IncludeFlowContributingAreasSwitch from './IncludeContributingBasinsSwitch';
import TimeSeriesProvider from './TimeSeriesProvider';
import TimeSeriesViz, {
  SOLO_VIZ_HEIGHT,
  SOLO_VIZ_HEIGHT_WITH_LEGEND,
  STACKED_VIZ_HEIGHT,
  STACKED_VIZ_HEIGHT_WITH_LEGEND,
} from './TimeSeriesViz';
import {Translator, useTranslations} from './TranslationsContext';
import Viz from './Viz';
import {
  VizConfig,
  VizConfigCreatorItem,
  filterConfigToAvailableTimeSeries,
  hfGetStyle,
  makeConfigItems,
  makeRequestFromVizConfig,
} from './Viz/config';
import WithWidth from './WithWidth';

type SeasonalVizMode = 'distribution' | 'traces';
const SEASONAL_MODES: SeasonalVizMode[] = ['distribution', 'traces'];

type TimeAggregationKey = '10 day' | '30 day' | '90 day' | 'monthly' | 'quarterly';

const TIME_AGGREGATIONS: {[key in TimeAggregationKey]: TimeAggregation} = {
  '10 day': '10D',
  '30 day': '30D',
  '90 day': '90D',
  monthly: 'MS',
  quarterly: 'QS',
};

const DEFAULT_TIME_AGGREGATION_KEY = '10 day';

type AggState = {
  timeAggregation: TimeAggregation;
  rateVolumeMode: RateVolumeMode;
  seasonalMode?: SeasonalVizMode;
};

function AggOptions({
  site,
  agg,
  updateAgg,
  showLevelOption = true,
}: {
  site: Site;
  agg: AggState;
  updateAgg: (update: Partial<AggState>) => void;
  showLevelOption?: boolean;
}) {
  const {t} = useTranslations();
  return (
    <div className="space-x-2">
      {agg.seasonalMode && (
        <select
          className={tw.select}
          value={agg.seasonalMode}
          onChange={(event) => {
            const nextMode = event.target.value as SeasonalVizMode;
            updateAgg({seasonalMode: nextMode});
            recordEvent('aggregation_option_changed', {
              section: 'seasonal',
              option: 'seasonal_mode',
              value: nextMode,
            });
          }}
        >
          {SEASONAL_MODES.map((seasonalMode) => (
            <option key={seasonalMode} value={seasonalMode}>
              {t(seasonalMode)}
            </option>
          ))}
        </select>
      )}
      <select
        className={tw.select}
        value={agg.rateVolumeMode}
        onChange={(event) => {
          if (
            ['rate', 'volume', 'cumulative-volume', 'cumulative-volume-from-now'].includes(
              event.target.value
            )
          ) {
            const newMode = event.target.value as RateVolumeMode;
            updateAgg({rateVolumeMode: newMode});
            recordEvent('aggregation_option_changed', {
              section: 'seasonal',
              option: 'rate_volume_mode',
              value: newMode,
            });
          }
        }}
      >
        <option value="rate">{t('rate')}</option>
        <option value="volume">{t('volume')}</option>
        <option value="cumulative-volume">{t('cumulative-volume')}</option>
        <option value="cumulative-volume-from-now">{t('cumulative-volume-from-now')}</option>
        {showLevelOption && site.seriesTypes.includes(SeriesType.Level) && (
          <option value="level">{t('level')}</option>
        )}
      </select>
      <select
        className={tw.select}
        value={agg.timeAggregation}
        onChange={(event) => {
          const timeAgg = event.target.value as TimeAggregation;
          updateAgg({timeAggregation: timeAgg});
          recordEvent('aggregation_option_changed', {
            section: 'seasonal',
            option: 'time_aggregation',
            value: timeAgg,
          });
        }}
      >
        {Object.keys(TIME_AGGREGATIONS).map((aggKey) => {
          const timeAgg = TIME_AGGREGATIONS[aggKey as TimeAggregationKey];
          return (
            <option key={aggKey} value={timeAgg}>
              {t(aggKey as TimeAggregationKey)}
            </option>
          );
        })}
      </select>
    </div>
  );
}

export default function Seasonal2({
  site,
  seriesType: seasonalFlowSeriesType,
  basinAveragingEnabled,
  defaultDateRange: defaultDateRangeProp,
  currentUser,
  isStaging,
  setIsStaging,
  seasonalModelVisualMode,
  includeFlowContributingAreas,
  setIncludeFlowContributingAreas,
}: {
  site: Site;
  seriesType: SeasonalSeriesType;
  basinAveragingEnabled: boolean;
  defaultDateRange?: [Date, Date];
  currentUser: ApiUserProfile | null;
  isStaging: boolean;
  setIsStaging: (isStaging: boolean) => void;
  seasonalModelVisualMode: SeasonalModelVisualMode;
  includeFlowContributingAreas: boolean;
  setIncludeFlowContributingAreas: (value: boolean) => void;
}) {
  const {t} = useTranslations();
  const {shouldShowStagingForecastToggles} = useStagingForecastToggle();
  const section = seasonalDashboardSection(seasonalFlowSeriesType);
  const horizon_days = getSeasonalHorizon(site);
  let history_days = 91; // ~3 months
  if (site.seriesTypes.includes(SeriesType.FlowSeasonalRollingExtended)) {
    history_days = 181; // ~6 months
  }

  const defaultDateRange = React.useMemo(
    () =>
      defaultDateRangeProp ||
      ([
        now(site.localTimeZone).minus({days: history_days}).startOf('day').toJSDate(),
        now(site.localTimeZone).plus({days: horizon_days}).endOf('day').toJSDate(),
      ] as [Date, Date]),
    [site.localTimeZone, defaultDateRangeProp, horizon_days, history_days]
  );

  const DateRangeQueryParam = React.useMemo(
    () => makeDateRangeQueryParam(defaultDateRange),
    [defaultDateRange]
  );

  const [dateRange, setDateRange] = useQueryParam('seasonalDateRange', DateRangeQueryParam);

  const [hfAgg, updateHfAgg] = React.useReducer(
    (
      state: AggState & {seasonalMode: SeasonalVizMode},
      action: Partial<AggState & {seasonalMode: SeasonalVizMode}>
    ) => {
      return {...state, ...action};
    },
    {
      timeAggregation: TIME_AGGREGATIONS[DEFAULT_TIME_AGGREGATION_KEY],
      rateVolumeMode: getDefaultRateVolumeMode(site),
      seasonalMode: SEASONAL_MODES[0],
    }
  );

  React.useEffect(() => {
    updateHfAgg({rateVolumeMode: getDefaultRateVolumeMode(site)});
  }, [site]);

  const [weatherAgg, updateWeatherAgg] = React.useReducer(
    (state: AggState, action: Partial<AggState>) => {
      return {...state, ...action};
    },
    {
      timeAggregation: TIME_AGGREGATIONS[DEFAULT_TIME_AGGREGATION_KEY],
      rateVolumeMode: 'rate',
    }
  );

  React.useEffect(() => {
    if (isInvalidLevelMode(weatherAgg.rateVolumeMode, site)) {
      updateWeatherAgg({rateVolumeMode: 'rate'});
    }
  }, [weatherAgg.rateVolumeMode, site, updateWeatherAgg]);

  // hfAgg and weatherAgg are eventually validated in the useEffect,
  // but we need to ensure they are valid on initial render to avoid noisy api errors
  const hfRateVolumeMode = makeValidRateVolumeMode(hfAgg.rateVolumeMode, site);
  const weatherRateVolumeMode = makeValidRateVolumeMode(weatherAgg.rateVolumeMode, site);

  const hfConfig = filterConfigToAvailableTimeSeries(
    makeHFConfig(
      site,
      seasonalFlowSeriesType,
      hfAgg.timeAggregation,
      hfRateVolumeMode,
      hfAgg.seasonalMode,
      t
    ),
    site
  );
  const weatherConfig = filterConfigToAvailableTimeSeries(
    makeWeatherConfig(
      weatherAgg.timeAggregation,
      weatherRateVolumeMode,
      includeFlowContributingAreas,
      t
    ),
    site
  );

  const forecastSources = [...hfConfig.forecasts, ...weatherConfig.forecasts].map((c) => c.source);

  const adjustedDateRange = [dateRange[0], addSeconds(dateRange[1], 1)] as [Date, Date];

  const {
    primaryForecastTimes,
    selectedPrimaryForecastTimes,
    selectedForecastTimesBySource,
    setSelectedPrimaryForecastTimes,
    onNextPrimaryForecastTimesChange,
  } = useSelectedForecastTimes(
    site,
    dateRange,
    horizon_days,
    Source.HydroForecastSeasonal,
    forecastSources,
    true,
    isStaging
  );

  // We conditionally show the contents of this component, but never unmount the Seasonal2 component itself,
  // even if we switch to showing Seasonal-3 via the staging toggle. This means we keep the state
  // of the dropdowns etc. so aggstate doesn't get lost when you switch between staging and nonstaging.
  const shouldShowSeasonal2Plot: boolean =
    seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal2Release ||
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3CustomerPreview ||
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3InternalPreview;

  if (!shouldShowSeasonal2Plot) {
    return <></>;
  }

  return (
    <div className={tw.section}>
      <div className={tw.sectionHeader}>
        <div className="flex items-center">
          <h2 className={tw.sectionTitle}>{t(DashboardSection.Seasonal)}</h2>
          <>
            <InformationCircleIcon
              className="w-5 h-5 text-gray-500 ml-2"
              data-tooltip-id="seasonal-2-model-info-tooltip"
            />
            <Tooltip
              id="seasonal-2-model-info-tooltip"
              className="z-[9999] max-w-md !bg-gray-50 !rounded-md !text-black !text-sm !font-normal"
              place="bottom"
              opacity={1}
              border="1px solid lightgray"
            >
              <p>{t('seasonal2ModelInfo')}</p>
            </Tooltip>
          </>
        </div>

        <div className="flex items-center space-x-4">
          {/* Show the staging toggle only if it's allowed by the feature flag & we don't already have Seasonal-3 available in preview mode */}
          {featureFlags.STAGING_TOGGLE(currentUser) &&
            shouldShowStagingForecastToggles &&
            seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal2Release && (
              <>
                <Switch
                  id="staging-toggle"
                  checked={isStaging}
                  onChange={() => setIsStaging(!isStaging)}
                  className={`${isStaging ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${isStaging ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                  />
                </Switch>
                <label htmlFor="staging-toggle" className="text-sm text-gray-600">
                  {t('stagingForecasts')}
                </label>
              </>
            )}
          <ForecastSelect
            horizon={horizon_days}
            site={site}
            dateRange={dateRange}
            defaultDateRange={defaultDateRange}
            setDateRange={setDateRange}
            primaryForecastTimes={primaryForecastTimes}
            selectedPrimaryForecastTimes={selectedPrimaryForecastTimes}
            setSelectedPrimaryForecastTimes={setSelectedPrimaryForecastTimes}
            onNextPrimaryForecastTimesChange={onNextPrimaryForecastTimesChange}
            shortcuts={{
              absolute: [
                {
                  displayName: t('latest'),
                  getSelection: (forecastTimes: ForecastTime[]) => [forecastTimes[0]],
                },
                {
                  displayName: t('latestYTD'),
                  getSelection: (forecastTimes: ForecastTime[]) => {
                    const latest = forecastTimes[0];
                    return [latest];
                  },
                  startDate: new Date(new Date().getFullYear(), 0, 1),
                  endDate: addDays(new Date(), horizon_days),
                },
                {
                  displayName: t('latestAndLastWeek'),
                  getSelection: (forecastTimes: ForecastTime[]) => {
                    const latest = forecastTimes[0];
                    const lastWeekInitializationTime = addUTCDays(latest.initializationTime, -7);
                    const lastWeek = forecastTimes.find(
                      (t) => t.initializationTime.getTime() === lastWeekInitializationTime.getTime()
                    )!;

                    return [latest, lastWeek];
                  },
                },
              ],
              relative: [
                {
                  displayName: t('every10Days'),
                  getSelection: makeGetSelectionForIntervalShortcuts(
                    selectedPrimaryForecastTimes,
                    [10, 20, 30],
                    addDays(dateRange[1], -10)
                  ),
                  startDate: dateRange[0],
                  endDate: dateRange[1],
                },
                {
                  displayName: t('every30Days'),
                  getSelection: makeGetSelectionForIntervalShortcuts(
                    selectedPrimaryForecastTimes,
                    [30, 60, 90],
                    addDays(dateRange[1], -30)
                  ),
                  startDate: dateRange[0],
                  endDate: dateRange[1],
                },
              ],
            }}
          />
        </div>
      </div>
      <WithWidth>
        {(width) => (
          <div className={tw.sectionBody}>
            <TimeSeriesProvider
              site={site}
              section={section}
              forecastTimesBySource={selectedForecastTimesBySource}
              dateRange={adjustedDateRange}
              isStaging={isStaging}
              {...makeRequestFromVizConfig(hfConfig)}
            >
              {(vizProps) => {
                return (
                  <Viz
                    section={t(DashboardSection.Seasonal)}
                    title={hfRateVolumeMode === 'level' ? t('waterLevel') : t('flow')}
                    renderOptions={() => (
                      <AggOptions
                        site={site}
                        agg={{...hfAgg, rateVolumeMode: hfRateVolumeMode}}
                        updateAgg={updateHfAgg}
                      />
                    )}
                    site={site}
                    loadingStateDims={[width, SOLO_VIZ_HEIGHT_WITH_LEGEND]}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    config={hfConfig}
                    {...vizProps}
                  >
                    {(props) => (
                      <TimeSeriesViz
                        {...props}
                        timeZone={site.localTimeZone}
                        dims={[width, SOLO_VIZ_HEIGHT]}
                      />
                    )}
                  </Viz>
                );
              }}
            </TimeSeriesProvider>
            <TimeSeriesProvider
              site={site}
              section={section}
              forecastTimesBySource={Object.keys(selectedForecastTimesBySource).reduce(
                (acc, source) => {
                  // this is a terrible hack to subtract a day (24 hours) from the GEFS issue
                  // time because any forecast younger than 28 hours (100800 seconds) will not
                  // have the full horizon.
                  if (source === Source.GEFS) {
                    const adjustmentThreshold = addSeconds(new Date(), -100800);
                    return {
                      ...acc,
                      [source]: selectedForecastTimesBySource[source]?.map((ft) => {
                        if (ft.initializationTime > adjustmentThreshold) {
                          return {
                            initializationTime: addDays(ft.initializationTime, -1),
                            issueTime: addDays(ft.issueTime, -1),
                          };
                        }
                        return ft;
                      }),
                    };
                  }
                  return acc;
                },
                {} as ForecastTimesBySource
              )}
              dateRange={adjustedDateRange}
              {...makeRequestFromVizConfig(weatherConfig)}
            >
              {(vizProps) => {
                return (
                  <Viz
                    section={t(DashboardSection.Seasonal)}
                    title={t('weather')}
                    renderOptions={() => (
                      <>
                        <IncludeFlowContributingAreasSwitch
                          site={site}
                          basinAveragingEnabled={basinAveragingEnabled}
                          includeFlowContributingAreas={includeFlowContributingAreas}
                          setIncludeFlowContributingAreas={setIncludeFlowContributingAreas}
                        />
                        <AggOptions
                          site={site}
                          agg={{...weatherAgg, rateVolumeMode: weatherRateVolumeMode}}
                          updateAgg={updateWeatherAgg}
                          showLevelOption={false}
                        />
                      </>
                    )}
                    site={site}
                    loadingStateDims={[width, STACKED_VIZ_HEIGHT_WITH_LEGEND * 2]}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    config={weatherConfig}
                    {...vizProps}
                  >
                    {(props) => {
                      return (
                        <>
                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            range={[0, 'auto']}
                            minMaxRangeConstraints={[null, 0.1]}
                            groupId={t('precipitation')}
                            dims={[width, STACKED_VIZ_HEIGHT]}
                          />

                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            groupId={t('temperature')}
                            dims={[width, STACKED_VIZ_HEIGHT]}
                            renderSvg={({y, graphDims, observationTimeSeriesList}) => (
                              <FreezingLine
                                y={y}
                                graphDims={graphDims}
                                temperatureTimeSeries={
                                  observationTimeSeriesList.find(
                                    (ts) => !!ts.variables[WeatherColumns.TEMPERATURE]
                                  )!
                                }
                              />
                            )}
                          />
                        </>
                      );
                    }}
                  </Viz>
                );
              }}
            </TimeSeriesProvider>
          </div>
        )}
      </WithWidth>
    </div>
  );
}

function makeHFConfig(
  site: Site,
  seasonalFlowSeriesType: SeasonalSeriesType,
  timeAggregation: TimeAggregation,
  rateVolumeMode: RateVolumeMode,
  seasonalMode: SeasonalVizMode,
  t: Translator
): VizConfig {
  let forecastConfigCreators: VizConfigCreatorItem[] = [];
  if (seasonalMode === 'distribution') {
    forecastConfigCreators = [
      {
        displayName: 'HydroForecast 90% CI',
        source: Source.HydroForecastSeasonal,
        columns: [FlowForecastColumns.DISCHARGE_0_05, FlowForecastColumns.DISCHARGE_0_95],
        getStyle: () => ({
          fill: COLORS.HF_90,
          fillOpacity: 0.3,
        }),
        legendIcon: 'rect',
        curveFactory: d3.curveStepAfter,
        hideIfMultipleForecastTimes: true,
      },
      {
        displayName: 'HydroForecast 50% CI',
        source: Source.HydroForecastSeasonal,
        columns: [FlowForecastColumns.DISCHARGE_0_25, FlowForecastColumns.DISCHARGE_0_75],
        getStyle: () => ({
          fill: COLORS.HF_50,
          fillOpacity: 0.6,
        }),
        legendIcon: 'rect',
        curveFactory: d3.curveStepAfter,
        hideIfMultipleForecastTimes: true,
      },
      {
        displayName: `HydroForecast ${t('mean')}`,
        source: Source.HydroForecastSeasonal,
        columns: [FlowForecastColumns.DISCHARGE_MEAN],
        getStyle: hfGetStyle,
        legendIcon: 'line',
        curveFactory: d3.curveStepAfter,
        showForecastTimeMarkers: true,
      },
      {
        displayName: `HydroForecast ${t('median')}`,
        source: Source.HydroForecastSeasonal,
        columns: [FlowForecastColumns.DISCHARGE_MEDIAN],
        getStyle: hfGetStyle,
        legendIcon: 'line',
        defaultNotVisible: true,
        isHidden: true,
        curveFactory: d3.curveStepAfter,
      },
    ];
  } else if (seasonalMode === 'traces') {
    const seasonalForecastQuery = site.forecastTimeSeries[seasonalFlowSeriesType].find(
      (ts) => ts.source === Source.HydroForecastSeasonal
    );
    // get all numeric columns (hopefully all years) from the site
    const yearColumns = seasonalForecastQuery
      ? seasonalForecastQuery.columns!.filter((c) => !isNaN(c as any))
      : [];

    forecastConfigCreators = [
      {
        displayName: `HydroForecast ${t('traces')}`,
        source: Source.HydroForecastSeasonalAllTraces,
        columns: yearColumns,
        getStyle: () => ({
          stroke: COLORS.OBS,
          strokeWidth: 0.5,
          strokeOpacity: 0.5,
        }),
        legendIcon: 'line',
        curveFactory: d3.curveStepAfter,
        hideIfMultipleForecastTimes: true,
        hideTooltip: true,
      },
      {
        displayName: `HydroForecast ${t('mean')}`,
        source: Source.HydroForecastSeasonal,
        columns: [FlowForecastColumns.DISCHARGE_MEAN],
        getStyle: () => ({
          ...hfGetStyle(),
          strokeWidth: 4,
        }),
        legendIcon: 'line',
        curveFactory: d3.curveStepAfter,
        showForecastTimeMarkers: true,
      },
    ];
  }

  const hasNwrfcEspNatural =
    site.forecastTimeSeries[seasonalFlowSeriesType].findIndex(
      (q) => q.source === Source.NWRFC_ESP_NATURAL
    ) > -1;

  const hasCnrfcEspNatural =
    site.forecastTimeSeries[seasonalFlowSeriesType].findIndex(
      (q) => q.source === Source.CNRFC_ESP_NATURAL
    ) > -1;

  const hasRfcEspNatural = hasNwrfcEspNatural || hasCnrfcEspNatural;

  let RfcSource: Source | undefined;
  let RfcDisplay: string | undefined;

  if (hasCnrfcEspNatural) {
    RfcSource = Source.CNRFC_ESP_NATURAL;
    RfcDisplay = 'NOAA CNRFC ESP Natural';
  }
  if (hasNwrfcEspNatural) {
    RfcSource = Source.NWRFC_ESP_NATURAL;
    RfcDisplay = 'NOAA NWRFC ESP Natural';
  }
  if (hasRfcEspNatural && RfcSource && RfcDisplay) {
    forecastConfigCreators.push({
      displayName: RfcDisplay,
      source: RfcSource,
      columns: [FlowForecastColumns.DISCHARGE_MEAN],
      getStyle: (idx = 0, numForecasts = 0) => {
        let strokeColor;
        if (numForecasts === 0) {
          const rfcColors = getRFCColors(0);
          strokeColor = rfcColors[rfcColors.length - 1];
        } else {
          const rfcColors = getRFCColors(numForecasts);
          strokeColor = rfcColors[idx];
        }
        return {stroke: strokeColor, strokeDasharray: 5};
      },
      legendIcon: 'line',
      showForecastTimeMarkers: true,
      curveFactory: d3.curveStepAfter,
    });
  }

  return {
    forecasts: makeConfigItems('forecasts', forecastConfigCreators, {
      timeAggregation,
      rateVolumeMode,
    }),
    observations: makeConfigItems(
      'observations',
      [
        {
          displayName: t('historicalMean'),
          source: Source.FLOW_HISTORICAL_DAILY,
          columns: [FlowHistoricalDailyColumns.FLOW_MEAN],
          getStyle: () => ({stroke: COLORS.OBS, strokeDasharray: 2}),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          isHidden: isCumulativeMode(rateVolumeMode),
        },
        ...site.observationTimeSeries[seasonalFlowSeriesType]
          .filter(
            (q) => FLOW_OBSERVATION_SOURCES.includes(q.source) && q.columns && q.columns.length
          )
          .flatMap((q) => {
            // trend is based on the presence of the observed_clean column, which
            // is added by the api at request-time. If this is present, we will add
            // an additional timeseries, and style the Observed timeseries
            // differently, and toggle its visibility off by default
            const hasTrend =
              site.observationTimeSeries[seasonalFlowSeriesType].findIndex(
                (q) =>
                  FLOW_OBSERVATION_SOURCES.includes(q.source) &&
                  q.columns &&
                  q.columns.includes(FlowObservationColumns.observed_clean)
              ) > -1;

            // because "Observed" can come in so many flavors, we use anything with a
            // valid Observation Source as observed, and trust the column provided by
            // the API completely
            const cs: VizConfigCreatorItem[] = [
              {
                displayName: t('observed'),
                source: q.source,
                columns: [q.columns![0]] as AllColumns[],
                getStyle: () => ({
                  hfStrokeOutlineStyle: {
                    stroke: 'white',
                    strokeWidth: 3,
                  },
                  stroke: COLORS.OBS,
                  strokeWidth: 2,
                  strokeOpacity: hasTrend ? 0.4 : 1,
                  fill: rateVolumeMode === 'level' ? COLORS.OBS : undefined,
                  fillOpacity: rateVolumeMode === 'level' ? 0.05 : undefined,
                }),
                legendIcon: 'line' as VizConfigCreatorItem['legendIcon'],
                curveFactory: d3.curveStepAfter,
                defaultNotVisible: hasTrend,
                isHidden: isCumulativeMode(rateVolumeMode),
              },
            ];

            if (hasTrend) {
              cs.push({
                displayName: t('observedClean'),
                source: q.source,
                columns: [FlowObservationColumns.observed_clean],
                getStyle: () => ({
                  hfStrokeOutlineStyle: {
                    stroke: 'white',
                    strokeWidth: 3,
                  },
                  stroke: COLORS.OBS,
                  strokeWidth: 2,
                  strokeOpacity: 1,
                  fill: rateVolumeMode === 'level' ? COLORS.OBS : undefined,
                  fillOpacity: rateVolumeMode === 'level' ? 0.05 : undefined,
                }),
                legendIcon: 'line',
                curveFactory: d3.curveStepAfter,
                isHidden: isCumulativeMode(rateVolumeMode),
              });
            }

            return cs;
          }),
      ],
      {timeAggregation, rateVolumeMode}
    ),
  };
}

function makeWeatherConfig(
  timeAggregation: TimeAggregation,
  rateVolumeMode: RateVolumeMode,
  includeFlowContributingAreas: boolean,
  t: Translator
): VizConfig {
  return {
    forecasts: makeConfigItems(
      'forecasts',
      [
        {
          displayName: `GEFS ${t('forecast')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.PRECIPITATION],
          getStyle: () => ({
            stroke: COLORS.WEATHER_3,
            fill: COLORS.WEATHER_3,
            fillOpacity: 0.1,
          }),
          legendIcon: 'rect',
          curveFactory: d3.curveStepAfter,
          showForecastTimeMarkers: true,
          groupId: t('precipitation'),
        },
        {
          displayName: `GEFS ${t('forecast')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.TEMPERATURE],
          getStyle: () => ({
            stroke: COLORS.WEATHER_3,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          showForecastTimeMarkers: true,
          groupId: t('temperature'),
        },
      ],
      {timeAggregation, rateVolumeMode, includeFlowContributingAreas}
    ),
    observations: makeConfigItems(
      'observations',
      [
        {
          displayName: `GEFS ${t('historicalMean')}`,
          source: Source.GEFS_HISTORICAL_DAILY,
          columns: [WeatherHistoricalDailyColumns.PRECIPITATION_MEAN],
          getStyle: () => ({
            stroke: COLORS.OBS,
            strokeDasharray: 2,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          groupId: t('precipitation'),
        },
        {
          displayName: `GEFS ${t('historicalMean')}`,
          source: Source.GEFS_HISTORICAL_DAILY,
          columns: [WeatherHistoricalDailyColumns.TEMPERATURE_MEAN],
          getStyle: () => ({
            stroke: COLORS.OBS,
            strokeDasharray: 2,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          groupId: t('temperature'),
        },
        {
          displayName: `GEFS ${t('observed')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.PRECIPITATION],
          getStyle: () => ({
            stroke: COLORS.OBS,
            fill: COLORS.OBS,
            fillOpacity: 0.1,
          }),
          legendIcon: 'rect',
          curveFactory: d3.curveStepAfter,
          groupId: t('precipitation'),
          aggregationOptions: {includeFlowContributingAreas},
        },
        {
          displayName: `GEFS ${t('observed')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.TEMPERATURE],
          getStyle: () => ({
            stroke: COLORS.OBS,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          groupId: t('temperature'),
          aggregationOptions: {includeFlowContributingAreas},
        },
      ],
      {timeAggregation, rateVolumeMode}
    ),
  };
}
